import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import NotificationService from "../../services/notification.service";
import useAuthorization from "../../contexts/useAuthorization";
import { isUrgent, Notification } from "./types";

export const NOTIFICATIONS_PAGE_SIZE = 20;

// Real-time updates come from the SSE stream (/notifications/stream) via
// fetchEventSource (EventSource can't send the Authorization header). If the
// stream never establishes — endpoint missing, proxy strips SSE, etc. — we fall
// back to polling the lightweight count endpoint every 30s.
const COUNT_POLL_INTERVAL = 30_000;

export interface NotificationsContextType {
  items: Notification[];
  unreadCount: number;
  total: number;
  page: number;
  loading: boolean;
  isOpen: boolean;
  hasMore: boolean;
  /** The most recent unread urgent notification, surfaced as a pinned banner. */
  urgentNotification: Notification | null;
  open: () => void;
  close: () => void;
  toggle: () => void;
  loadPage: (page: number) => Promise<void>;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  dismissAll: () => Promise<void>;
}

const NotificationsContext = React.createContext<NotificationsContextType | null>(null);

export const NotificationsProvider = ({ children }: { children: React.ReactNode }) => {
  const { loggedIn } = useAuthorization();

  const [items, setItems] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Track the last unread count seen by the poller so we only refetch the feed
  // when the number actually grows (avoids hammering the list endpoint).
  const lastUnreadRef = useRef(0);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const loadPage = useCallback(
    async (nextPage: number) => {
      if (!loggedIn) return;
      setLoading(true);
      const res = await NotificationService.fetchNotifications({
        page: nextPage,
        limit: NOTIFICATIONS_PAGE_SIZE,
      });
      if (res) {
        setItems((prev) => (nextPage === 1 ? res.data : [...prev, ...res.data]));
        setTotal(res.total);
        setPage(nextPage);
      }
      setLoading(false);
    },
    [loggedIn]
  );

  const refreshUnread = useCallback(async () => {
    if (!loggedIn) return;
    const count = await NotificationService.fetchUnreadCount();
    if (count !== null) {
      setUnreadCount(count);
      lastUnreadRef.current = count;
    }
  }, [loggedIn]);

  // Pull both the first page and the badge count — used on login and as the
  // poller's "unread grew" handler.
  const refresh = useCallback(async () => {
    await Promise.all([loadPage(1), refreshUnread()]);
  }, [loadPage, refreshUnread]);

  // Apply a notification pushed over SSE. A new id is prepended; a known id (a
  // stream replay on reconnect, or a row the server rewrote in place, like a draft's
  // upload progress) replaces the row where it is; a row that arrives dismissed (the
  // server retired it, e.g. the draft was submitted) is dropped. Only a genuinely new
  // or newly dropped row moves the badge + total. The counters update live inside the
  // setItems updater so the lookup and the count can never disagree (and stay correct
  // under StrictMode).
  const applyFromStream = useCallback((incoming: Notification) => {
    setItems((prev) => {
      const current = prev.find((n) => n.id === incoming.id);
      if (incoming.isDismissed) {
        if (!current) {
          return prev;
        }
        setTotal((t) => Math.max(0, t - 1));
        if (!current.isRead) {
          setUnreadCount((c) => Math.max(0, c - 1));
          lastUnreadRef.current = Math.max(0, lastUnreadRef.current - 1);
        }
        return prev.filter((n) => n.id !== incoming.id);
      }
      // Keep the local read state: the client may have marked it read since the push.
      if (current) {
        return prev.map((n) => (n.id === incoming.id ? { ...incoming, isRead: n.isRead } : n));
      }
      setTotal((t) => t + 1);
      if (!incoming.isRead) {
        setUnreadCount((c) => c + 1);
        lastUnreadRef.current += 1;
      }
      return [incoming, ...prev];
    });
  }, []);

  const markRead = useCallback(
    async (id: string) => {
      const target = items.find((n) => n.id === id);
      if (!target || target.isRead) return;

      // Optimistic: flip the row + decrement the badge, then revert on failure.
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));

      const ok = await NotificationService.markAsRead(id);
      if (!ok) {
        setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: false } : n)));
        setUnreadCount((c) => c + 1);
      } else {
        lastUnreadRef.current = Math.max(0, lastUnreadRef.current - 1);
      }
    },
    [items]
  );

  const markAllRead = useCallback(async () => {
    const snapshot = items;
    const previousUnread = unreadCount;
    if (previousUnread === 0) return;

    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
    lastUnreadRef.current = 0;

    const ok = await NotificationService.markAllAsRead();
    if (!ok) {
      setItems(snapshot);
      setUnreadCount(previousUnread);
      lastUnreadRef.current = previousUnread;
    }
  }, [items, unreadCount]);

  const dismiss = useCallback(
    async (id: string) => {
      const target = items.find((n) => n.id === id);
      if (!target) return;
      const wasUnread = !target.isRead;

      // Optimistic: drop the row and resolve its unread contribution once. Keep
      // lastUnreadRef in lock-step so the poller doesn't treat the next (lower)
      // server count as "grew" and refetch the feed.
      setItems((prev) => prev.filter((n) => n.id !== id));
      setTotal((t) => Math.max(0, t - 1));
      if (wasUnread) {
        setUnreadCount((c) => Math.max(0, c - 1));
        lastUnreadRef.current = Math.max(0, lastUnreadRef.current - 1);
      }

      const ok = await NotificationService.dismiss(id);
      if (!ok) {
        // Revert: re-insert and re-sync from the server to restore ordering.
        setItems((prev) => [target, ...prev]);
        setTotal((t) => t + 1);
        if (wasUnread) {
          setUnreadCount((c) => c + 1);
          lastUnreadRef.current += 1;
        }
        return;
      }

      // Clearing also resolves the unread state ("mark as read and clear"). The
      // /count endpoint may tally unread rows regardless of dismissal, so without
      // this the next poll would resurrect the old badge value. The row is
      // soft-dismissed (not deleted), so marking it read still resolves.
      if (wasUnread) void NotificationService.markAsRead(id);
    },
    [items]
  );

  const dismissAll = useCallback(async () => {
    const snapshot = items;
    const previousTotal = total;
    const previousUnread = unreadCount;
    if (snapshot.length === 0) return;

    setItems([]);
    setTotal(0);
    setUnreadCount(0);
    lastUnreadRef.current = 0;

    const ok = await NotificationService.dismissAll();
    if (!ok) {
      setItems(snapshot);
      setTotal(previousTotal);
      setUnreadCount(previousUnread);
      lastUnreadRef.current = previousUnread;
      return;
    }

    // Same as single dismiss: resolve unread state server-side so the /count
    // poll doesn't bring the badge back if it ignores dismissal.
    if (previousUnread > 0) void NotificationService.markAllAsRead();
  }, [items, total, unreadCount]);

  // Initial load + reset on auth changes.
  useEffect(() => {
    if (loggedIn) {
      refresh();
    } else {
      setItems([]);
      setUnreadCount(0);
      setTotal(0);
      setPage(1);
      setIsOpen(false);
      lastUnreadRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  // Real-time updates: subscribe to the SSE stream and apply pushed items.
  // If the stream errors (endpoint missing, proxy strips SSE, repeated retries),
  // fall back to polling the count endpoint every 30s. Both paths are torn down
  // on unmount / logout.
  useEffect(() => {
    if (!loggedIn) return;

    let pollInterval: number | undefined;
    let pollingStarted = false;

    const startPolling = () => {
      if (pollingStarted) return;
      pollingStarted = true;
      pollInterval = window.setInterval(async () => {
        if (document.hidden) return; // skip polls while the tab is backgrounded
        const count = await NotificationService.fetchUnreadCount();
        if (count === null) return;
        setUnreadCount(count);
        if (count > lastUnreadRef.current) {
          await loadPage(1);
        }
        lastUnreadRef.current = count;
      }, COUNT_POLL_INTERVAL);
    };

    const disconnect = NotificationService.subscribe({
      onNotification: applyFromStream,
      // Stream errors are retried by the library; if we never recover, polling
      // keeps the badge fresh. startPolling() is idempotent.
      onError: startPolling,
    });

    return () => {
      disconnect();
      if (pollInterval !== undefined) window.clearInterval(pollInterval);
    };
  }, [loggedIn, loadPage, applyFromStream]);

  const urgentNotification = useMemo(
    () => items.find((n) => isUrgent(n.type) && !n.isRead) ?? null,
    [items]
  );

  const value = useMemo<NotificationsContextType>(
    () => ({
      items,
      unreadCount,
      total,
      page,
      loading,
      isOpen,
      hasMore: items.length < total,
      urgentNotification,
      open,
      close,
      toggle,
      loadPage,
      refresh,
      markRead,
      markAllRead,
      dismiss,
      dismissAll,
    }),
    [
      items,
      unreadCount,
      total,
      page,
      loading,
      isOpen,
      urgentNotification,
      open,
      close,
      toggle,
      loadPage,
      refresh,
      markRead,
      markAllRead,
      dismiss,
      dismissAll,
    ]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export default function useNotifications(): NotificationsContextType {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationsProvider");
  }
  return context;
}
